import React, { useState } from 'react';
import { Settings, ChevronDown, ChevronRight, Save, Eye, Download, Plus, Trash2, Edit2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Agreement Template Configuration Sidebar
 * 
 * Allows admin to configure what sections, fields, and data appear in the SaaS Agreement
 * All configuration is saved to database and used when generating agreements
 */
export default function AgreementFormatSidebar({ isOpen, onClose, config, onSave }) {
  const [localConfig, setLocalConfig] = useState(config || getDefaultConfig());
  const [expandedSections, setExpandedSections] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Update section configuration
  const updateSection = (sectionId, updates) => {
    setLocalConfig(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, ...updates } : s
      )
    }));
  };

  // Update field within a section
  const updateField = (sectionId, fieldId, updates) => {
    setLocalConfig(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id === sectionId) {
          return {
            ...s,
            fields: s.fields.map(f => 
              f.id === fieldId ? { ...f, ...updates } : f
            )
          };
        }
        return s;
      })
    }));
  };

  // Add new field to section
  const addField = (sectionId, fieldType) => {
    const newField = {
      id: `field_${Date.now()}`,
      label: 'New Field',
      type: fieldType,
      dataSource: 'static',
      value: '',
      visible: true,
      required: false
    };

    setLocalConfig(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id === sectionId) {
          return {
            ...s,
            fields: [...s.fields, newField]
          };
        }
        return s;
      })
    }));
  };

  // Delete field
  const deleteField = (sectionId, fieldId) => {
    setLocalConfig(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id === sectionId) {
          return {
            ...s,
            fields: s.fields.filter(f => f.id !== fieldId)
          };
        }
        return s;
      })
    }));
  };

  // Save configuration
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(localConfig);
      toast.success('Agreement template configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl border-l border-slate-200 z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-slate-200 p-4 z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-bold text-slate-900">Agreement Template</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Config'}
          </button>
        </div>
      </div>

      {/* Configuration Sections */}
      <div className="p-4 space-y-4">
        {/* Global Settings */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-3">Global Settings</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Agreement Title
              </label>
              <input
                type="text"
                value={localConfig.title}
                onChange={(e) => setLocalConfig({ ...localConfig, title: e.target.value })}
                className="input-field text-sm w-full"
                placeholder="No Term, Non-Exclusive SaaS Agreement"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={localConfig.companyName}
                onChange={(e) => setLocalConfig({ ...localConfig, companyName: e.target.value })}
                className="input-field text-sm w-full"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Company Address
              </label>
              <textarea
                value={localConfig.companyAddress}
                onChange={(e) => setLocalConfig({ ...localConfig, companyAddress: e.target.value })}
                className="input-field text-sm w-full"
                rows="2"
              />
            </div>
          </div>
        </div>

        {/* Sections */}
        {localConfig.sections.map((section) => (
          <SectionEditor
            key={section.id}
            section={section}
            isExpanded={expandedSections[section.id]}
            onToggle={() => toggleSection(section.id)}
            onUpdate={(updates) => updateSection(section.id, updates)}
            onUpdateField={(fieldId, updates) => updateField(section.id, fieldId, updates)}
            onAddField={(fieldType) => addField(section.id, fieldType)}
            onDeleteField={(fieldId) => deleteField(section.id, fieldId)}
            editingField={editingField}
            setEditingField={setEditingField}
          />
        ))}
      </div>
    </div>
  );
}

// Section Editor Component
function SectionEditor({ 
  section, 
  isExpanded, 
  onToggle, 
  onUpdate, 
  onUpdateField, 
  onAddField, 
  onDeleteField,
  editingField,
  setEditingField
}) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <div
        onClick={onToggle}
        className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-600" />
          )}
          <span className="font-semibold text-sm text-slate-900">{section.title}</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={section.visible}
              onChange={(e) => {
                e.stopPropagation();
                onUpdate({ visible: e.target.checked });
              }}
              className="rounded"
            />
            <span className="text-slate-600">Show</span>
          </label>
        </div>
      </div>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-3 space-y-3 bg-white">
          {/* Section Description */}
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Section Description
            </label>
            <textarea
              value={section.description || ''}
              onChange={(e) => onUpdate({ description: e.target.value })}
              className="input-field text-xs w-full"
              rows="2"
              placeholder="Optional description for this section"
            />
          </div>

          {/* Fields */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-900">Fields</label>
              <button
                onClick={() => onAddField('text')}
                className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 rounded"
              >
                <Plus className="w-3 h-3" />
                Add Field
              </button>
            </div>

            {section.fields?.map((field) => (
              <FieldEditor
                key={field.id}
                field={field}
                isEditing={editingField === field.id}
                onEdit={() => setEditingField(field.id)}
                onSave={() => setEditingField(null)}
                onUpdate={(updates) => onUpdateField(field.id, updates)}
                onDelete={() => onDeleteField(field.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Field Editor Component
function FieldEditor({ field, isEditing, onEdit, onSave, onUpdate, onDelete }) {
  return (
    <div className="bg-slate-50 rounded p-2 border border-slate-200">
      {!isEditing ? (
        // Display Mode
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-900">{field.label}</span>
              <span className="text-xs px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded">
                {field.type}
              </span>
              {field.dataSource === 'database' && (
                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                  DB: {field.dbField}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <label className="flex items-center gap-1 mr-2">
              <input
                type="checkbox"
                checked={field.visible}
                onChange={(e) => onUpdate({ visible: e.target.checked })}
                className="w-3 h-3 rounded"
              />
              <span className="text-xs text-slate-600">Show</span>
            </label>
            <button
              onClick={onEdit}
              className="p-1 hover:bg-slate-200 rounded"
            >
              <Edit2 className="w-3 h-3 text-slate-600" />
            </button>
            <button
              onClick={onDelete}
              className="p-1 hover:bg-red-100 rounded"
            >
              <Trash2 className="w-3 h-3 text-red-600" />
            </button>
          </div>
        </div>
      ) : (
        // Edit Mode
        <div className="space-y-2">
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Label</label>
            <input
              type="text"
              value={field.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              className="input-field text-xs w-full"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Field Type</label>
            <select
              value={field.type}
              onChange={(e) => onUpdate({ type: e.target.value })}
              className="input-field text-xs w-full"
            >
              <option value="text">Text</option>
              <option value="textarea">Textarea</option>
              <option value="number">Number</option>
              <option value="currency">Currency</option>
              <option value="percentage">Percentage</option>
              <option value="date">Date</option>
              <option value="select">Select/Dropdown</option>
              <option value="table">Table</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Data Source</label>
            <select
              value={field.dataSource}
              onChange={(e) => onUpdate({ dataSource: e.target.value })}
              className="input-field text-xs w-full"
            >
              <option value="static">Static Text</option>
              <option value="database">Database Field</option>
              <option value="calculated">Calculated</option>
            </select>
          </div>

          {field.dataSource === 'database' && (
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Database Field Path</label>
              <input
                type="text"
                value={field.dbField || ''}
                onChange={(e) => onUpdate({ dbField: e.target.value })}
                className="input-field text-xs w-full font-mono"
                placeholder="e.g., services.0.proposed_pricing.paymentRates.CC.takeRate"
              />
            </div>
          )}

          {field.dataSource === 'static' && (
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Default Value</label>
              <textarea
                value={field.value || ''}
                onChange={(e) => onUpdate({ value: e.target.value })}
                className="input-field text-xs w-full"
                rows="2"
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => onUpdate({ required: e.target.checked })}
                className="rounded"
              />
              <span className="text-slate-700">Required</span>
            </label>
          </div>

          <div className="flex gap-2 pt-2 border-t border-slate-200">
            <button
              onClick={onSave}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-emerald-600 text-white rounded text-xs"
            >
              <Check className="w-3 h-3" />
              Save
            </button>
            <button
              onClick={onSave}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-slate-200 text-slate-700 rounded text-xs"
            >
              <X className="w-3 h-3" />
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Default Configuration based on PDF analysis
function getDefaultConfig() {
  return {
    title: 'No Term, Non-Exclusive SaaS Agreement',
    companyName: 'Government Window, LLC',
    companyAddress: '125 Townpark Drive NW. Suite 500, Kennesaw GA 30144',
    
    sections: [
      {
        id: 'header',
        title: 'Header & Title',
        visible: true,
        description: 'Agreement title and company branding',
        fields: [
          {
            id: 'title',
            label: 'Agreement Title',
            type: 'text',
            dataSource: 'static',
            value: 'No Term, Non-Exclusive SaaS Agreement',
            visible: true,
            required: true
          },
          {
            id: 'company_logo',
            label: 'Company Logo',
            type: 'text',
            dataSource: 'static',
            value: 'GOVERNMENT WINDOW',
            visible: true,
            required: false
          }
        ]
      },
      {
        id: 'principal_info',
        title: 'Principal Information',
        visible: true,
        description: 'Company (Principal) details',
        fields: [
          {
            id: 'principal_name',
            label: 'Principal Name',
            type: 'text',
            dataSource: 'static',
            value: 'Government Window, LLC',
            visible: true,
            required: true
          },
          {
            id: 'principal_address',
            label: 'Principal Address',
            type: 'textarea',
            dataSource: 'static',
            value: '125 Townpark Drive NW. Suite 500, Kennesaw GA 30144',
            visible: true,
            required: true
          }
        ]
      },
      {
        id: 'end_user_info',
        title: 'End-User (Licensee) Information',
        visible: true,
        description: 'Client/customer details',
        fields: [
          {
            id: 'licensee_name',
            label: 'End-User (Licensee)',
            type: 'text',
            dataSource: 'database',
            dbField: 'proposal.client_name',
            visible: true,
            required: true
          },
          {
            id: 'agency_name',
            label: 'Agency Name',
            type: 'text',
            dataSource: 'database',
            dbField: 'proposal.agency_name',
            visible: true,
            required: false
          },
          {
            id: 'contact_name',
            label: 'Contact Name',
            type: 'text',
            dataSource: 'database',
            dbField: 'proposal.contact_name',
            visible: true,
            required: true
          },
          {
            id: 'contact_phone',
            label: 'Contact Phone',
            type: 'text',
            dataSource: 'database',
            dbField: 'proposal.contact_phone',
            visible: true,
            required: true
          },
          {
            id: 'address',
            label: 'Address',
            type: 'textarea',
            dataSource: 'database',
            dbField: 'proposal.client_address',
            visible: true,
            required: true
          }
        ]
      },
      {
        id: 'grant_statement',
        title: 'Grant Statement',
        visible: true,
        description: 'License grant introductory text',
        fields: [
          {
            id: 'grant_text',
            label: 'Grant Text',
            type: 'textarea',
            dataSource: 'static',
            value: 'This agreement sets out the terms upon which the Principal grants the use of certain software applications referred to as the Licensed Items, by way of Software as a Service ("SaaS"), to the End-User listed below.',
            visible: true,
            required: true
          }
        ]
      },
      {
        id: 'schedule_a',
        title: 'Schedule A - Licensed Items',
        visible: true,
        description: 'Service modules and pricing',
        fields: [
          {
            id: 'intro_text',
            label: 'Introduction Text',
            type: 'textarea',
            dataSource: 'static',
            value: 'Schedule "A" lists the service fees for each Module ("Licensed Items") selected below. The End-User will not be charged service fees until they launch the respective Module.',
            visible: true,
            required: false
          },
          {
            id: 'function_instruction',
            label: 'Function Column Instructions',
            type: 'textarea',
            dataSource: 'static',
            value: 'In the "Function" column, place a letter next to the Module(s) you will be launching. [W]=Webpay, [P]=Point of Sale, [I]=IVR, [T]=ITR',
            visible: true,
            required: false
          },
          {
            id: 'modules_table',
            label: 'Modules Pricing Table',
            type: 'table',
            dataSource: 'database',
            dbField: 'services',
            visible: true,
            required: true,
            tableConfig: {
              columns: [
                { id: 'module', label: 'Module', dbField: 'service_name', width: '20%' },
                { id: 'function', label: 'Function', dbField: 'function_codes', width: '10%' },
                { id: 'cc_rate', label: 'CC Rate', dbField: 'proposed_pricing.paymentRates.CC.takeRate', type: 'percentage', width: '12%' },
                { id: 'cc_min', label: 'CC Min', dbField: 'proposed_pricing.paymentRates.CC.minFee', type: 'currency', width: '12%' },
                { id: 'amex_rate', label: 'AmEx Rate', dbField: 'proposed_pricing.paymentRates.AMEX.takeRate', type: 'percentage', width: '12%' },
                { id: 'amex_min', label: 'AmEx Min', dbField: 'proposed_pricing.paymentRates.AMEX.minFee', type: 'currency', width: '12%' },
                { id: 'echeck', label: 'E-check', dbField: 'proposed_pricing.paymentRates.ACH.minFee', type: 'currency', width: '12%' }
              ]
            }
          }
        ]
      },
      {
        id: 'stipulations',
        title: 'Stipulations & Notes',
        visible: true,
        description: 'Additional terms and custom notes',
        fields: [
          {
            id: 'stip_header',
            label: 'Section Header',
            type: 'text',
            dataSource: 'static',
            value: 'Stipulations & Notes: (please define:)',
            visible: true,
            required: false
          },
          {
            id: 'cost_allocation_text',
            label: 'Cost Allocation Text',
            type: 'textarea',
            dataSource: 'static',
            value: 'Cost allocation fees based on a percentage of each transaction amount and/or a minimum fee per transaction...',
            visible: true,
            required: false
          },
          {
            id: 'custom_notes',
            label: 'Custom Notes',
            type: 'textarea',
            dataSource: 'database',
            dbField: 'agreement.notes',
            visible: true,
            required: false
          }
        ]
      },
      {
        id: 'signatures',
        title: 'Signature Blocks',
        visible: true,
        description: 'Signature fields for both parties',
        fields: [
          {
            id: 'principal_sig',
            label: 'Principal Signature Block',
            type: 'text',
            dataSource: 'static',
            value: 'Include signature, name, title, date fields',
            visible: true,
            required: true
          },
          {
            id: 'end_user_sig',
            label: 'End-User Signature Block',
            type: 'text',
            dataSource: 'static',
            value: 'Include signature, name, title, date fields',
            visible: true,
            required: true
          }
        ]
      },
      {
        id: 'legal_terms',
        title: 'Legal Terms (Pages 3-4)',
        visible: true,
        description: 'Standard legal clauses',
        fields: [
          {
            id: 'include_definitions',
            label: 'Include Definitions Section',
            type: 'select',
            dataSource: 'static',
            value: 'yes',
            visible: true,
            required: true
          },
          {
            id: 'include_license_terms',
            label: 'Include License Terms',
            type: 'select',
            dataSource: 'static',
            value: 'yes',
            visible: true,
            required: true
          },
          {
            id: 'include_termination',
            label: 'Include Termination Clause',
            type: 'select',
            dataSource: 'static',
            value: 'yes',
            visible: true,
            required: true
          },
          {
            id: 'include_warranty',
            label: 'Include Warranty Disclaimers',
            type: 'select',
            dataSource: 'static',
            value: 'yes',
            visible: true,
            required: true
          },
          {
            id: 'governing_law',
            label: 'Governing Law',
            type: 'text',
            dataSource: 'static',
            value: 'State of Georgia',
            visible: true,
            required: true
          }
        ]
      }
    ]
  };
}
